// Max-bid calculator (#3209): the most to bid so the deal still clears the
// buyer's margin. Pure, so the arithmetic is tested on its own.
//
// max bid = ARV x (1 - margin - closing costs) - repairs - holding costs
//
// margin and closing costs are fractions of ARV (after-repair value); repairs
// and holding costs are dollars. The 70% rule is margin 0.20 + costs 0.10 with
// no holding. ARV is the user's number: Parcely has no sold comps for most of
// NC (a last-sale date is on well under 1% of live rows), so the county's
// estimate is only a starting point, never a valuation.

export interface BidInputs {
  /** After-repair value, dollars. */
  arv: number | null;
  /** Repair budget, dollars. */
  repairs: number | null;
  /** Holding costs (taxes, insurance, loan interest) over the hold, dollars. */
  holding: number | null;
  /** Buying and selling costs as a fraction of ARV (0.08 = 8%). */
  costsPct: number;
  /** Target profit as a fraction of ARV (0.15 = 15%). */
  marginPct: number;
}

export const DEFAULT_BID_INPUTS: BidInputs = {
  arv: null,
  repairs: null,
  holding: null,
  costsPct: 0.08,
  marginPct: 0.15,
};

export interface BidResult {
  /** The max bid, dollars; null until an ARV is entered. Never negative. */
  maxBid: number | null;
  /** True when costs, margin and repairs eat the whole ARV (no bid works). */
  underwater: boolean;
}

const dollars = (n: number | null | undefined): number => (n != null && Number.isFinite(n) && n > 0 ? n : 0);

/** Clamp a fraction to a sane 0..0.9 so a typo cannot flip the sign. */
export function clampPct(n: number): number {
  return Number.isFinite(n) ? Math.min(0.9, Math.max(0, n)) : 0;
}

export function computeMaxBid(i: BidInputs): BidResult {
  if (i.arv == null || !Number.isFinite(i.arv) || i.arv <= 0) return { maxBid: null, underwater: false };
  const raw = i.arv * (1 - clampPct(i.marginPct) - clampPct(i.costsPct)) - dollars(i.repairs) - dollars(i.holding);
  return { maxBid: Math.max(0, Math.round(raw)), underwater: raw <= 0 };
}

export interface BidComparison {
  /** max bid minus the reference figure; positive = room under the max. */
  room: number;
  /** True when the reference is already above the max bid. */
  overMax: boolean;
}

/** Compare the max bid with the figure to beat (current bid or amount owed). */
export function compareToReference(maxBid: number | null, reference: number | null): BidComparison | null {
  if (maxBid == null || reference == null || !(reference > 0)) return null;
  const room = maxBid - reference;
  return { room, overMax: room < 0 };
}
